/**
 * Who is signed in, asked once and shared by the shell.
 *
 * `status` separates the states the shell draws differently: still asking,
 * signed out, signed in -- and a deploy that is not configured yet, which is
 * not a signed-out visitor and must not be shown a sign-in form that cannot
 * work (how illgati's worst deploy felt from the outside).
 */

import { useCallback, useEffect, useState } from "react";
import { SIGN_IN_REFUSALS, type OrgSummary, type SessionUser } from "@utuwrk/core";
import { ApiError, api } from "./api.js";

export type SessionStatus = "loading" | "in" | "out" | "unconfigured" | "unreachable";

export interface Session {
  status: SessionStatus;
  user: SessionUser | null;
  /** The organisations the person is an active member of. */
  organizations: OrgSummary[];
  /** What the worker says is missing, when `status` is "unconfigured". */
  missing: string[];
  /** The sentence to show when `status` is "unreachable". */
  problem: string | null;
  refresh: () => Promise<void>;
  /** `everywhere` ends every session the account holds, on every device. */
  signOut: (everywhere?: boolean) => Promise<void>;
}

export function useSession(): Session {
  const [status, setStatus] = useState<SessionStatus>("loading");
  const [user, setUser] = useState<SessionUser | null>(null);
  const [organizations, setOrganizations] = useState<OrgSummary[]>([]);
  const [missing, setMissing] = useState<string[]>([]);
  const [problem, setProblem] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    try {
      const { user: next, organizations: theirs } = await api.me();
      setUser(next);
      setOrganizations(theirs ?? []);
      setMissing([]);
      setProblem(null);
      setStatus(next ? "in" : "out");
    } catch (error) {
      setUser(null);
      setOrganizations([]);
      if (error instanceof ApiError && error.missing?.length) {
        setMissing(error.missing);
        setStatus("unconfigured");
      } else {
        setProblem(error instanceof Error ? error.message : String(error));
        setStatus("unreachable");
      }
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const signOut = useCallback(async (everywhere = false) => {
    try {
      await (everywhere ? api.signOutEverywhere() : api.signOut());
    } catch (error) {
      // The cookie may still be live, so say so rather than doing nothing.
      setProblem(error instanceof Error ? error.message : String(error));
      setStatus("unreachable");
      return;
    }
    // The worker has cleared the cookie; the answer is known without asking again.
    setUser(null);
    setOrganizations([]);
    setStatus("out");
  }, []);

  return { status, user, organizations, missing, problem, refresh, signOut };
}

/**
 * The token from a mailed link, which opens `/signin?token=...`. Opening it
 * claims nothing -- mail scanners open links -- so it is only read here, and
 * the screen asks for one press before anything is spent.
 */
export function linkToken(location: { pathname: string; search: string }): string | null {
  if (location.pathname.replace(/\/+$/, "") !== "/signin") return null;
  const token = new URLSearchParams(location.search).get("token");
  return token ? token : null;
}

/**
 * Why a Google or Microsoft sign-in was refused, as a sentence. The worker's
 * callback is a navigation and cannot answer JSON, so it lands on
 * `/signin?error=<key>`; only a key core knows is shown, never words from the
 * address bar, so a crafted link cannot put its own text on this screen.
 */
export function signInRefusal(location: { pathname: string; search: string }): string | null {
  if (location.pathname.replace(/\/+$/, "") !== "/signin") return null;
  const key = new URLSearchParams(location.search).get("error");
  if (!key) return null;
  return Object.hasOwn(SIGN_IN_REFUSALS, key)
    ? SIGN_IN_REFUSALS[key as keyof typeof SIGN_IN_REFUSALS]
    : SIGN_IN_REFUSALS["oauth-failed"];
}
